import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken, getToken, API_BASE } from "@/lib/api";
import { Loader2, LogOut, Download, RefreshCw, Search, FileText, X, ExternalLink, Briefcase, Mail, Newspaper } from "lucide-react";

const STATUSES = ["All", "New", "Contacted", "Supplier Search", "Quote Sent", "Closed"];
const statusColor = {
  "New": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Contacted": "bg-amber-400/10 text-amber-300 border-amber-400/30",
  "Supplier Search": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  "Quote Sent": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "Closed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setAdmin(me.data);
        await loadAll("All");
      } catch (e) {
        setToken(null);
        navigate("/admin/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll(statusValue) {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/admin/enquiries", { params: statusValue && statusValue !== "All" ? { status: statusValue } : {} }),
        api.get("/admin/enquiries/stats"),
      ]);
      setItems(list.data.items || []);
      setStats(st.data || { total: 0, by_status: {} });
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setToken(null);
    navigate("/admin/login");
  }

  async function applyFilter(s) {
    setFilter(s);
    await loadAll(s);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      return (
        (it.full_name || "").toLowerCase().includes(q) ||
        (it.company_name || "").toLowerCase().includes(q) ||
        (it.email || "").toLowerCase().includes(q) ||
        (it.product_category || "").toLowerCase().includes(q) ||
        (it.country || "").toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  async function exportCsv() {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/enquiries/export`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vyrtrade-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function updateEnquiry(id, payload) {
    const { data } = await api.patch(`/admin/enquiries/${id}`, payload);
    setItems((prev) => prev.map((p) => (p.id === id ? data : p)));
    setSelected(data);
    const st = await api.get("/admin/enquiries/stats");
    setStats(st.data);
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="border-r border-white/5 bg-black/40 px-5 py-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-7 w-7 rounded-sm bg-gradient-to-br from-blue-600 to-blue-500 grid place-items-center">
            <span className="font-display font-bold text-white text-sm">V</span>
          </div>
          <div>
            <div className="font-display font-bold tracking-tight text-[14px]">VYR Trade</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500">ADMIN CONSOLE</div>
          </div>
        </Link>

        <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">VIEWS</div>
        <div className="flex flex-col gap-1 mb-6">
          <Link
            to="/admin"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 border bg-zinc-900 border-white/10 text-white"
            data-testid="nav-enquiries"
          >
            <Briefcase className="h-3.5 w-3.5" /> Sourcing Enquiries
          </Link>
          <Link
            to="/admin/messages"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent"
            data-testid="nav-messages"
          >
            <Mail className="h-3.5 w-3.5" /> Contact Messages
          </Link>
          <Link
            to="/admin/insights"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent"
            data-testid="nav-insights"
          >
            <Newspaper className="h-3.5 w-3.5" /> Insights
          </Link>
        </div>

        <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">FILTERS</div>
        <div className="flex flex-col gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => applyFilter(s)}
              className={`text-left px-3 py-2 rounded-md text-[13px] flex items-center justify-between border transition-colors ${
                filter === s ? "bg-zinc-900 border-white/10 text-white" : "border-transparent text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span>{s}</span>
              <span className="font-mono text-[11px] text-zinc-500 tnum">
                {s === "All" ? stats.total : stats.by_status?.[s] || 0}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-auto pt-6 border-t border-white/5">
          <div className="text-[12px] text-zinc-400 break-all">{admin?.email}</div>
          <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-[12px] text-zinc-400 hover:text-white" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <section className="min-w-0">
        <div className="border-b border-white/5 bg-zinc-900/40 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">SOURCING ENQUIRIES</div>
            <div className="font-display text-2xl font-semibold mt-1">{stats.total} <span className="text-zinc-500 text-base font-normal">total</span></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company, email, country…"
                className="pl-9 pr-3 py-2 bg-zinc-950 border border-white/10 rounded-md text-[13px] w-72 focus:border-amber-400/60 focus:outline-none"
                data-testid="admin-search"
              />
            </div>
            <button onClick={() => loadAll(filter)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/10 rounded-md text-[13px] hover:bg-white/5" data-testid="admin-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-md text-[13px] text-white" data-testid="admin-export-csv">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5 border-b border-white/5">
          {STATUSES.slice(1).map((s) => (
            <div key={s} className="bg-zinc-950 p-4">
              <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500">{s.toUpperCase()}</div>
              <div className="mt-1 font-display text-2xl font-bold tnum">{stats.by_status?.[s] || 0}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="p-6">
          <div className="border border-white/5 rounded-lg overflow-hidden bg-zinc-900/40">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-950/60 text-zinc-500 font-mono text-[11px] tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">ID</th>
                    <th className="text-left px-4 py-3">DATE</th>
                    <th className="text-left px-4 py-3">COMPANY</th>
                    <th className="text-left px-4 py-3">CONTACT</th>
                    <th className="text-left px-4 py-3">COUNTRY</th>
                    <th className="text-left px-4 py-3">CATEGORY</th>
                    <th className="text-left px-4 py-3">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500" data-testid="admin-empty">No enquiries yet.</td></tr>
                  ) : (
                    filtered.map((e) => (
                      <tr key={e.id} className="border-t border-white/5 hover:bg-zinc-900 cursor-pointer" onClick={() => setSelected(e)} data-testid={`row-${e.id}`}>
                        <td className="px-4 py-3 font-mono text-zinc-400">{e.id.slice(0, 8).toUpperCase()}</td>
                        <td className="px-4 py-3 text-zinc-400 font-mono">{new Date(e.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-zinc-100">{e.company_name}</td>
                        <td className="px-4 py-3 text-zinc-300">{e.full_name} <span className="text-zinc-500">· {e.email}</span></td>
                        <td className="px-4 py-3 text-zinc-300">{e.country}</td>
                        <td className="px-4 py-3 text-zinc-300">{e.product_category}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded-md text-[11px] font-mono border ${statusColor[e.status] || ""}`}>
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {selected && (
        <DetailDrawer enquiry={selected} onClose={() => setSelected(null)} onUpdate={updateEnquiry} />
      )}
    </div>
  );
}

function DetailDrawer({ enquiry, onClose, onUpdate }) {
  const [status, setStatus] = useState(enquiry.status);
  const [notes, setNotes] = useState(enquiry.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(enquiry.status);
    setNotes(enquiry.notes || "");
  }, [enquiry.id]);

  async function save() {
    setSaving(true);
    try {
      await onUpdate(enquiry.id, { status, notes });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="detail-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl h-full bg-zinc-950 border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-zinc-950/95 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">ENQUIRY · {enquiry.id.slice(0, 8).toUpperCase()}</div>
            <div className="mt-1 font-display text-lg font-semibold">{enquiry.company_name}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white" data-testid="drawer-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <KV k="Contact" v={`${enquiry.full_name} · ${enquiry.email}`} />
          <KV k="Phone / WhatsApp" v={enquiry.phone} mono />
          <KV k="Country" v={enquiry.country} />
          <KV k="Category" v={enquiry.product_category} />
          <KV k="Quantity" v={enquiry.target_quantity || "-"} />
          <KV k="Target price" v={enquiry.target_price || "-"} />
          <KV k="Certifications" v={enquiry.certifications || "-"} />
          <KV k="Timeline" v={enquiry.timeline || "-"} />
          <div>
            <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">PRODUCT DESCRIPTION</div>
            <div className="mt-1 text-[14px] text-zinc-200 whitespace-pre-wrap">{enquiry.product_description}</div>
          </div>
          {enquiry.message && (
            <div>
              <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">MESSAGE</div>
              <div className="mt-1 text-[14px] text-zinc-300 whitespace-pre-wrap">{enquiry.message}</div>
            </div>
          )}
          {enquiry.file_path && (
            <div>
              <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">ATTACHMENT</div>
              <a
                href={`${API_BASE}/admin/files/${enquiry.file_path}`}
                onClick={async (e) => {
                  e.preventDefault();
                  const res = await fetch(`${API_BASE}/admin/files/${enquiry.file_path}`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                  });
                  if (!res.ok) return;
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                }}
                className="mt-1 inline-flex items-center gap-2 text-[13px] text-amber-400 hover:text-amber-300"
                data-testid="drawer-attachment"
              >
                <FileText className="h-4 w-4" /> {enquiry.file_name || "Download attachment"} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          )}

          <div className="pt-4 border-t border-white/5">
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-2">UPDATE</div>
            <label className="block text-[12px] text-zinc-400 mb-1">Status</label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-zinc-950 border border-white/10 rounded-md px-3 py-2 text-[14px] focus:border-amber-400/60 focus:outline-none"
              data-testid="drawer-status"
            >
              {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="block text-[12px] text-zinc-400 mt-3 mb-1">Notes</label>
            <textarea
              rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-zinc-950 border border-white/10 rounded-md px-3 py-2 text-[14px] focus:border-amber-400/60 focus:outline-none"
              placeholder="Internal notes — supplier list, next steps, etc."
              data-testid="drawer-notes"
            />
            <button
              onClick={save} disabled={saving}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-60"
              data-testid="drawer-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">{k.toUpperCase()}</div>
      <div className={`col-span-2 text-[14px] text-zinc-200 ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}
